import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppLayoutWithHeader } from '@/components/layout/AppLayoutWithHeader';
import { ContentContainer } from '@/components/layout/ContentContainer';
import { NewKPICards } from '@/components/dashboard/NewKPICards';
import { CampaignsTable } from '@/components/dashboard/CampaignsTable';
import { Button } from '@/components/ui/button';
import { BuyCreditModal } from '@/components/credits/BuyCreditModal';
import { Plus } from 'lucide-react';
import { useDashboardData } from '@/contexts/DashboardDataContext';
import { useAuth } from '@/hooks/useAuth';
import { useCredits } from '@/hooks/useCreditBalance';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/productionLogger';

/**
 * ✅ PHASE 1B : Composant interne qui consomme le context
 * 
 * Ce composant est rendu DANS AppLayoutWithHeader, donc a accès au Provider
 */
const DashboardContent = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // ✅ PHASE 1B: Consommer depuis le context (Provider dans AppLayoutWithHeader)
  const {
    stats,
    campaigns,
    campaignsLoading,
    campaignsFetched, // ✅ Pour gate l'empty state
    refetchCampaigns,
  } = useDashboardData();
  
  const { profile } = useAuth();
  const { toast } = useToast();
  
  // État pour la modale d'achat de crédits
  const [showBuyCreditModal, setShowBuyCreditModal] = useState(false);
  
  // Hook pour vérifier le solde de crédits
  const organizationId = profile?.organization_id || undefined;
  const { availableCredits, hasCredits, loading: loadingCredits } = useCredits(organizationId);
  
  // Récupérer le filtre depuis l'URL
  const urlFilter = searchParams.get('filter');
  const lastToastFilter = useRef<string | null>(null);
  
  // Afficher un toast quand on arrive sur une vue filtrée (une seule fois)
  useEffect(() => {
    if (urlFilter && urlFilter !== lastToastFilter.current) {
      let message = '';
      if (urlFilter === 'overdue') {
        message = 'Campagnes hors délais';
      } else if (urlFilter === 'fraud') {
        message = 'Campagnes avec suspicion de fraude';
      }
      
      if (message) {
        toast({
          title: message,
          description: "Vue filtrée active",
        });
        lastToastFilter.current = urlFilter;
      }
    } else if (!urlFilter) {
      lastToastFilter.current = null;
    }
  }, [urlFilter, toast]);

  // Gérer la création d'une nouvelle campagne avec vérification des crédits
  // Requirement 1.1: Vérifier le solde avant de créer la campagne
  // Requirement 3.1: Afficher la modale d'achat si crédits insuffisants
  const handleNewCampaign = () => {
    logger.debug('New campaign button clicked', {
      organizationId,
      availableCredits,
      loadingCredits
    });

    // Si on est encore en train de charger les crédits, attendre
    if (loadingCredits) {
      logger.debug('Credits still loading, waiting...');
      return;
    }

    // Vérifier si l'utilisateur a des crédits disponibles
    if (!hasCredits(1)) {
      logger.warn('Insufficient credits for new campaign', {
        organizationId,
        availableCredits
      });

      // Afficher la modale d'achat de crédits
      // Le bandeau LowCreditBanner est déjà affiché en haut de la page
      setShowBuyCreditModal(true);
      return;
    }

    // L'utilisateur a des crédits, naviguer vers la page de création
    logger.debug('User has credits, navigating to campaign creation', {
      organizationId,
      availableCredits
    });

    navigate('/create-campaign');
  };

  return (
    <ContentContainer className="w-full">
      <div className="flex w-full flex-col gap-4">
        <NewKPICards
          stats={{
            refused_campaigns: stats?.refused_campaigns ?? 0,
            pending_candidate: stats?.pending_candidate_campaigns ?? 0,
            pending_referents: stats?.pending_referents_campaigns ?? 0,
            completed_campaigns: stats?.completed_campaigns ?? 0,
          }}
        />

        <div className="app-surface rounded-[22px] p-4 md:p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-[18px] font-semibold tracking-[-0.02em] text-[hsl(var(--app-text-strong))] md:text-[20px]">
                Campagnes
              </h2>
            </div>
            <Button
              onClick={handleNewCampaign}
              className="h-10 rounded-[14px] px-5 text-sm sm:self-start"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle campagne
            </Button>
          </div>

          <div className="mt-4">
            <CampaignsTable 
            campaigns={campaigns} 
            loading={campaignsLoading}
            fetched={campaignsFetched} // ✅ Passer pour gate l'empty state
            urlFilter={urlFilter}
            onResetFilter={() => setSearchParams({})}
            refetchCampaigns={refetchCampaigns}
          />
          </div>
        </div>
      </div>

      {/* Modale d'achat de crédits */}
      <BuyCreditModal
        open={showBuyCreditModal}
        onOpenChange={setShowBuyCreditModal}
      />
    </ContentContainer>
  );
};

/**
 * ✅ PHASE 1B : Dashboard wrapper avec AppLayoutWithHeader
 * 
 * AppLayoutWithHeader contient le Provider, donc DashboardContent a accès au context
 */
const Dashboard = () => {
  return (
    <AppLayoutWithHeader
      title="Prise de référence 🔎"
      layoutMode="default"
      sidebarVisible={true}
    >
      <DashboardContent />
    </AppLayoutWithHeader>
  );
};

export default Dashboard;
